import { Component, AfterViewInit, Renderer2, Inject, OnInit, OnDestroy } from '@angular/core';
import AOS from 'aos';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RelevantTechgeekComponent } from '../relevant-techgeek/relevant-techgeek.component';
import { ResourcesService } from '../../../services/resources.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

interface ArticlePost {
  id: number;
  title: string;
  image: string;
  slug?: string;
}

@Component({
  selector: 'app-techgeek-1',
  imports: [CommonModule, RelevantTechgeekComponent, ReactiveFormsModule, HttpClientModule],
  standalone: true,
  providers: [ResourcesService],
  templateUrl: './techgeek-1.component.html',
  styleUrl: './techgeek-1.component.scss'
})
export class Techgeek1Component implements AfterViewInit,OnDestroy,OnInit {
  isModalOpen = false;
  isSubmitting = false;
  submitSuccess = false;
  errorMessage = '';
  downloadForm!: FormGroup;
  private jsonLdScript: any;

  readonly techGeekTitle = "Tech Geek Ed. 1";
  constructor(
    private fb: FormBuilder,
    private resourcesService: ResourcesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.downloadForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern('^[A-Za-z]+$')]], // Added pattern for consistency with apply-position
      email: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{3,4}$')]]
    });
  }

  openModal() {
    this.isModalOpen = true;
    this.submitSuccess = false;
    this.errorMessage = '';
    this.isSubmitting = false;
    this.downloadForm.reset();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onSubmit() {
    if (this.downloadForm.valid) {

      this.isSubmitting = true;
      this.errorMessage = '';

      const payload = {
        Name: this.downloadForm.value.name,
        Email: this.downloadForm.value.email,
        TechGeekTitle: this.techGeekTitle
      };

      this.resourcesService.sendTechGeekEmail(payload).subscribe({
        next: (response: any) => {
          if (response && !response.IsError) {
            this.submitSuccess = true;
            this.isSubmitting = false;

            setTimeout(() => {
              this.closeModal();
            }, 4000);
          } else {
            this.isSubmitting = false;
            this.errorMessage = response?.Message;
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.errorMessage = "Server error. Please try again.";
        }
      });
    }
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngAfterViewInit() {
    AOS.init();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

injectSchemaMarkup(): void {
    const techGeek1Schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-1",
          "url": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-1",
          "name": "Tech Geek Ed. 1 — Kalpita Technologies Tech Magazine",
          "description": "Kalpita Tech Geek Magazine Edition 1 (Q4 2022) explores emerging technology trends across app inventions including AR, AI, and ONDC, and a comparative analysis of VR, AR, and MR devices.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".softwaredev-heading",
              ".takeaways-section .takeaway-item p",
              ".section-title"
            ]
          },
          "significantLink": ["https://heyzine.com/flip-book/d4d26dded7.html"],
          "datePublished": "2022-10-01",
          "dateModified": "2022-12-31",
          "isPartOf": { "@type": "WebSite", "@id": "https://www.kalpitatechnologies.com/#website" }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-1#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Tech Geek", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek" },
            { "@type": "ListItem", "position": 3, "name": "Tech Geek Ed. 1", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-1" }
          ]
        },
        {
          "@type": "TechArticle",
          "headline": "Tech Geek Ed. 1: Emerging Technology Trends Q4 2022",
          "articleBody": "Kalpita Tech Geek Magazine Edition 1 covers App Inventions (Houzz, Wanna Kicks, Midjourney, ONDC), Research and Development (Anti-Pollution Helmets, Healthcare Robotics), and Tech Difference (VR/AR/MR comparisons).",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "associatedMedia": {
            "@type": "MediaObject",
            "contentUrl": "https://heyzine.com/flip-book/d4d26dded7.html",
            "name": "Tech Geek Ed. 1 — Digital Flipbook Edition"
          }
        },
        {
          "@type": "HowTo",
          "name": "How to Stay Ahead of Tech Trends Using Tech Geek Insights",
          "step": [
            { "@type": "HowToStep", "name": "Explore AR Apps", "text": "Investigate applications like Houzz or Wanna Kicks to see how AR improves consumer decisions." },
            { "@type": "HowToStep", "name": "Leverage AI Creation", "text": "Experiment with Midjourney for AI image generation and creative marketing." },
            { "@type": "HowToStep", "name": "Evaluate ONDC", "text": "Assess government-backed digital commerce platforms to lower business entry barriers." },
            { "@type": "HowToStep", "name": "Monitor R&D", "text": "Track wearable tech like anti-pollution helmets or medical robotic capsules." },
            { "@type": "HowToStep", "name": "Compare Devices", "text": "Evaluate VR/AR hardware like Meta Quest Pro and Google Glass based on price and FoV." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is Kalpita Tech Geek?",
              "acceptedAnswer": { "@type": "Answer", "text": "A community-driven magazine published by Kalpita Technologies to help technocrats discover the latest technological developments." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(techGeek1Schema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  //    articles: ArticlePost[] = [
  //   {
  //     id: 1,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-one.webp'
  //   },
  //   {
  //     id: 2,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-two.webp'
  //   },
  //   {
  //     id: 3,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-three.webp'
  //   },
  //   {
  //     id: 4,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-four.webp'
  //   },
  //   {
  //     id: 5,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-five.webp'
  //   },
  //   {
  //     id: 6,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-six.webp'
  //   },
  //   {
  //     id: 7,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-seven.webp'
  //   },
  //   {
  //     id: 8,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-eight.webp'
  //   }
  // ];
  onArticleClick(article: ArticlePost): void {
    console.log('Article clicked:', article);
    // Handle navigation to article detail page
  }

  trackByArticle(index: number, article: ArticlePost): number {
    return article.id;
  }
}
